import React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { compose } from 'react-apollo';
import { Order_orders_data } from '@api/graphql/types';
import AssociatedLinks from './AssociatedLinks';
import DataRow from '@components/DataRow';
import Price from './Price';

type Props = {
    order: Order_orders_data;
} & InjectedIntlProps;

class OrderProfilePrint extends React.Component<Props> {
    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.order.${id}` });
    }

    private formatInfo = (info: string | null) => {
        if (!info) { return null; }

        try {
            const parsed: {} = JSON.parse(info);
            let formattedText: string = '';

            Object.keys(parsed).forEach((key, index) => {
                formattedText = formattedText.concat(`${this.label(`additional_information.key.${key}`)}: ${parsed[key]}`);
                if (index < Object.keys(parsed).length - 1) {
                    formattedText = formattedText.concat(', ');
                }
            });

            return formattedText;
        } catch {
            return info;
        }
    }

    public render() {
        const { order } = this.props;
        return order && (
            <>
                <div className="orderProfilePrintHeader">
                    <h2>{this.label('id.title')}: {order.id} - <strong>{order.total_amount} Ft</strong></h2>
                </div>
                <div className="orderProfilePrintAddresses">
                    {/* Shipping */}
                    <div>
                        <h3>{this.label('shipping_block.title')}</h3>
                        {order.shipping_name && (
                            <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('shipping_name'), value: order.shipping_name }} />
                        )}
                        {(order.shipping_postcode || order.shipping_city || order.shipping_street || order.shipping_house_number) && (
                            <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('shipping_address'), value: `${order.shipping_postcode} ${order.shipping_city}, ${order.shipping_street} ${order.shipping_house_number}` }} />
                        )}
                        <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('shipping_note'), value: order.shipping_note }} />
                    </div>
                    {/* Billing */}
                    <div>
                        <h3>{this.label('billing_block.title')}</h3>
                        {order.billing_name && (
                            <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('billing_name'), value: order.billing_name }} />
                        )}
                        {(order.billing_postcode || order.billing_city || order.billing_street || order.billing_house_number) && (
                            <DataRow nameWidth={6} valueWidth={6} data={{ name: this.label('billing_address'), value: `${order.billing_postcode} ${order.billing_city}, ${order.billing_street} ${order.billing_house_number}` }} />
                        )}
                    </div>
                </div>
                <br /><br />

                {/* Details */}
                {(order.shipping_method && order.shipping_method.name) && <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('shipping_method.name.title'), value: order.shipping_method.name }} />}
                {(order.payment_method && order.payment_method.name) && <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('payment_method.name.title'), value: order.payment_method.name }} />}
                {(order.status && order.status.title) && <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('status.title.title'), value: order.status.title }} />}
                {(order.contact_data) && (order.contact_data.contact_name) && <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('contact_data.contact_name'), value: order.contact_data.contact_name }} />}
                {(order.contact_data) && (order.contact_data.contact_telephone) && <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('contact_data.contact_telephone'), value: order.contact_data.contact_telephone }} />}
                {(order.contact_data) && (order.contact_data.contact_email) && <DataRow nameWidth={3} valueWidth={9} data={{ name: this.label('contact_data.contact_email'), value: order.contact_data.contact_email }} />}
                {((order.shipping_method && order.shipping_method.name) || (order.payment_method && order.payment_method.name)) && (<><br /><br /></>)}

                {/* Shipping method */}
                {(order.post_point) ? (
                    <>
                        <hr />
                        <h3>{this.label('shipping_post_point.title')}</h3>
                        <div>
                            <strong>{this.label('shipping_address')}</strong>
                        </div>
                        <div>
                            {order.post_point.postcode} {order.post_point.city}, {order.post_point.street_name} {order.post_point.street_type} {order.post_point.house_number}
                        </div>
                    </>
                ) : (order.timetable_time_slot && (
                    <>
                        <hr />
                        <h3>{this.label('shipping_timetable.title')}</h3>
                        <div>
                            <strong>{this.label('sector')}</strong>
                        </div>
                        <div>
                            {order.timetable_time_slot.sector.name}
                        </div>
                        <div>
                            <strong>{this.label('range')}</strong>
                        </div>
                        <div>
                            {order.timetable_time_slot.range_date} {order.timetable_time_slot.timeRange.time_from}-{order.timetable_time_slot.timeRange.time_to}
                        </div>
                    </>
                ))}
                {/* Products */}
                {(order.order_products.length > 0) && (
                    <>
                        <hr />
                        <h3>{this.label('products.title')}</h3>
                        <table className="orderProfilePrintProducts">
                            <tbody>
                                {order.order_products.map((orderProduct) => {
                                    return (
                                        <tr key={orderProduct.product.id}>
                                            <td><strong>{orderProduct.product.name}</strong></td>
                                            <td><Price orderProduct={orderProduct} printMode={true} /></td>
                                            <td>
                                                {orderProduct.default_quantity_piece} {orderProduct.default_quantity_name}<br />
                                                {orderProduct.alternative_quantity_piece} {orderProduct.alternative_quantity_name}
                                            </td>
                                            <td>{this.formatInfo(orderProduct.additional_information)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                )}
                {/* Associated orders */}
                <AssociatedLinks order={order} printMode={true} />
                {/* ERP comment */}
                <hr />
                <h3>{this.label('erp_comment.title')}</h3>
                <div className="row">
                    <div className="col-sm-12">
                        {order.erp_comment || '---'}
                    </div>
                </div>
            </>
        );
    }
}


export default compose(
    injectIntl,
)(OrderProfilePrint);



// WEBPACK FOOTER //
// ./src/modules/Order/components/OrderProfilePrint.tsx